<template>
    <main v-show="entryResolved" class="creator-center-page" :class="{ 'deposit-layer-open': $route.name === 'creatorDepositDetail' }">
        <div class="creator-center-shell">
            <creator-center-header :user="user" />
            <creator-center-sidebar
                :items="navigation"
                :active-key="activeView"
                @select="selectView" />

            <section class="creator-workspace">
                <div class="creator-content-frame">
                    <keep-alive>
                        <identity-view
                            v-if="activeView === 'identity'"
                            :roles="roleList"
                            @photographer-submitted="reloadCreatorOverview"
                            @makeup-artist-submitted="reloadCreatorOverview"
                            :recovery-queue="recoveryQueue"
                            @recovery-submitted="handleRecoverySubmitted"
                            @recovery-state-changed="refreshRecoveryState"
                            @recovery-access-denied="handleRecoveryAccessDenied" />

                        <creator-order-hall v-else-if="activeView === 'orders-hall'" />

                        <creator-service-orders v-else-if="activeView === 'orders'" />

                        <creator-schedule v-else-if="activeView === 'schedule'" />

                        <creator-revenue-center v-else-if="activeView === 'income'" />

                        <section v-else class="creator-coming-soon" aria-live="polite">
                            <div>
                                <p class="creator-page-kicker">CREATOR CENTER</p>
                                <h2>{{ activeViewLabel }} · 暂未开放</h2>
                                <p>该能力正在为创作者体验持续打磨，敬请期待。</p>
                            </div>
                        </section>
                    </keep-alive>
                </div>
            </section>
        </div>

    </main>
</template>

<script>
import { mapState } from "vuex";
import CreatorCenterHeader from "@/components/creator-center/CreatorCenterHeader";
import CreatorCenterSidebar from "@/components/creator-center/CreatorCenterSidebar";
import IdentityView from "@/components/creator-center/IdentityView";
import CreatorOrderHall from "@/components/creator-center/CreatorOrderHall";
import CreatorServiceOrders from "@/components/creator-center/CreatorServiceOrders";
import CreatorSchedule from "@/components/creator-center/CreatorSchedule";
import CreatorRevenueCenter from "@/components/creator-center/CreatorRevenueCenter";
import request from "@/tool/request";
import API from "@/tool/api";
import { getAccessToken } from "@/tool/authStorage";
import {
    getResultData,
    getResultError,
    getResultMessage,
    isResultSuccess,
} from "@/tool/result";
import creatorCenter from "@/tool/creatorCenter.cjs";
import realNameCertification from "@/tool/realNameCertification.cjs";

const photographerPortrait = require("@/assets/creator-center/photographer.jpg");
const makeupPortrait = require("@/assets/creator-center/makeup.jpg");
const creatorCenterNavigation = Object.freeze([
    { key: "dashboard", label: "工作台" },
    { key: "orders-hall", label: "接单大厅" },
    { key: "identity", label: "我的身份" },
    { key: "orders", label: "订单管理" },
    { key: "schedule", label: "只读日程" },
    { key: "portfolio", label: "原创作品" },
    { key: "wardrobe", label: "服饰管理" },
    { key: "income", label: "收益中心" },
]);
const {
    getCreatorAccessState,
    getRequiredProfessionalRecoveries,
    normalizeCreatorOverview,
    normalizeProfessionalRecovery,
} = creatorCenter;
const {
    isRealNameStatusResponse,
    normalizeRealNameStatus,
} = realNameCertification;

export default {
    name: "CreatorCenter",
    components: {
        CreatorCenterHeader,
        CreatorCenterSidebar,
        IdentityView,
        CreatorOrderHall,
        CreatorServiceOrders,
        CreatorSchedule,
        CreatorRevenueCenter,
    },
    data() {
        return {
            entryResolved: false,
            overview: normalizeCreatorOverview(),
            recoveryQueue: [],
            verification: null,
            previewMode: false,
            activeView: "orders-hall",
            navigation: creatorCenterNavigation,
        };
    },
    computed: {
        ...mapState({
            basicData: (state) => state.user.basic_data || {},
            userName: (state) => state.user.user_name,
        }),
        user() {
            const nickname = this.previewMode
                ? "吉享er_7xmwts"
                : (this.basicData.nickname || this.basicData.user_name || this.basicData.userName || this.userName || "创作者");
            return {
                nickname,
                avatarUrl: this.basicData.head || "",
                certificationText: this.verification && this.verification.status === "APPROVED"
                    ? "已完成实名认证"
                    : "实名认证状态待确认",
                avatarText: nickname.slice(0, 1),
            };
        },
        roleList() {
            return [
                {
                    ...this.overview.photographer,
                    key: "photographer",
                    portrait: photographerPortrait,
                },
                {
                    ...this.overview.makeupArtist,
                    key: "makeup",
                    portrait: makeupPortrait,
                },
            ];
        },
        activeViewLabel() {
            const activeItem = this.navigation.find((item) => item.key === this.activeView);
            return activeItem ? activeItem.label : "创作者中心";
        },
    },
    methods: {
        redirectToIntro() {
            this.$router.replace({ name: "creatorIntro" });
        },
        resolveCreatorEntry() {
            if (!getAccessToken()) {
                this.redirectToIntro();
                return;
            }

            request.get(API.user.realNameVerification.status)
                .then((response) => {
                    if (!isResultSuccess(response)) {
                        throw getResultError(response, "实名认证状态查询失败");
                    }

                    const statusData = getResultData(response);
                    if (!isRealNameStatusResponse(statusData)) {
                        throw new Error("实名认证状态无效，请稍后重试");
                    }
                    return normalizeRealNameStatus(statusData);
                })
                .catch((error) => {
                    error.realNameStatusQueryFailed = true;
                    throw error;
                })
                .then((realNameStatus) => {
                    if (realNameStatus.status !== "APPROVED") {
                        this.redirectToIntro();
                        return null;
                    }

                    this.verification = realNameStatus;
                    return request.get(API.creatorCenter.overview);
                })
                .then((response) => {
                    if (!response) return null;
                    if (!isResultSuccess(response)) {
                        throw getResultError(response, "创作者中心状态查询失败");
                    }

                    const overview = normalizeCreatorOverview(getResultData(response) || {});
                    if (overview.joined !== true) {
                        this.redirectToIntro();
                        return null;
                    }

                    this.overview = overview;
                    return this.loadRecoveryQueue();
                })
                .then(() => {
                    if (this.overview.joined === true) this.entryResolved = true;
                })
                .catch((error) => {
                    if (error.realNameStatusQueryFailed) {
                        this.$message.error(getResultMessage(error, "实名认证状态查询失败，请稍后重试"));
                        this.redirectToIntro();
                        return;
                    }
                    const accessState = getCreatorAccessState(error);
                    if (accessState.type === "certification") {
                        this.redirectToIntro();
                        return;
                    }

                    this.$message.error(getResultMessage(error, "创作者中心入口查询失败，请稍后重试"));
                    this.redirectToIntro();
                });
        },
        reloadCreatorOverview() {
            return request.get(API.creatorCenter.overview)
                .then((response) => {
                    if (!isResultSuccess(response)) {
                        throw getResultError(response, "身份申请状态刷新失败");
                    }
                    this.overview = normalizeCreatorOverview(getResultData(response) || {});
                })
                .catch((error) => {
                    this.$message.error(getResultMessage(error, "身份申请已提交，状态刷新失败，请稍后刷新页面"));
                });
        },
        handleRecoverySubmitted() {
            this.$message.success("恢复资料已提交，本人真实照片审核通过前，该专业身份仍将保持暂停状态。");
            return this.refreshRecoveryState();
        },
        refreshRecoveryState() {
            return Promise.all([this.reloadCreatorOverview(), this.loadRecoveryQueue()]);
        },
        handleRecoveryAccessDenied() {
            this.entryResolved = false;
            this.$message.warning("实名认证状态已失效，请重新完成实名认证后再恢复资料。");
            this.redirectToIntro();
        },
        loadProfessionalRecovery(professionalType) {
            return request.get(API.creatorCenter[professionalType].recovery)
                .then((response) => {
                    if (!isResultSuccess(response)) throw getResultError(response, "恢复资料加载失败");
                    return normalizeProfessionalRecovery(getResultData(response) || {});
                })
                .catch((error) => {
                    const label = professionalType === "photographer" ? "摄影师" : "化妆师";
                    this.$message.warning(getResultMessage(error, `${label}恢复资料状态加载失败，请刷新后重试。`));
                    return normalizeProfessionalRecovery();
                });
        },
        loadRecoveryQueue() {
            return Promise.all([this.loadProfessionalRecovery("photographer"), this.loadProfessionalRecovery("makeupArtist")])
                .then(([photographerRecovery, makeupArtistRecovery]) => {
                this.recoveryQueue = getRequiredProfessionalRecoveries({
                    photographer: photographerRecovery,
                    makeupArtist: makeupArtistRecovery,
                });
            });
        },
        selectView(viewKey) {
            const routeNames = {
                "dashboard": "creatorDashboard",
                "orders-hall": "creatorOrderHall",
                "identity": "creatorIdentity",
                "orders": "creatorServiceOrders",
                "schedule": "creatorSchedule",
                "portfolio": "creatorPortfolio",
                "wardrobe": "creatorWardrobe",
                "income": "creatorRevenues",
            };
            this.$router.push({ name: routeNames[viewKey] });
        },
        syncViewFromRoute() {
            this.activeView = this.$route.meta.creatorView || "orders-hall";
        },
    },
    created() {
        this.syncViewFromRoute();
        if (process.env.NODE_ENV === "development" && ["order-hall", "orders", "income"].includes(this.$route.query.preview)) {
            this.previewMode = true;
            this.verification = { status: "APPROVED" };
            this.entryResolved = true;
            return;
        }
        this.resolveCreatorEntry();
    },
    watch: {
        "$route.name"() { this.syncViewFromRoute(); },
    },
};
</script>

<style>
.creator-center-page {
    min-width: 1180px;
    min-height: 100vh;
    color: #332d49;
    background: #fff;
    font-family: Inter, "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
    font-size: 15px;
    line-height: 1.5;
}

.creator-center-page *,
.creator-center-page *::before,
.creator-center-page *::after {
    box-sizing: border-box;
}

.creator-center-page button:focus-visible,
.creator-center-page input:focus-visible,
.creator-center-page textarea:focus-visible {
    outline: 3px solid rgba(200, 149, 223, .35);
    outline-offset: 2px;
}

.creator-center-shell {
    display: grid;
    grid-template-rows: 98px 1fr;
    grid-template-columns: 200px 1fr;
    min-height: 100vh;
}

.creator-workspace {
    grid-row: 2;
    min-width: 0;
    padding: 44px 44px 60px;
    overflow: auto;
}

.creator-content-frame {
    width: min(1180px, 100%);
    margin: 0 auto;
}

.creator-page-kicker {
    margin: 0 0 8px;
    color: #b35ec0;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .14em;
}

.creator-coming-soon {
    display: grid;
    min-height: 500px;
    place-items: center;
    padding: 56px;
    color: #746c7e;
    text-align: center;
    background: #fff;
    border: 1px dashed #dbc8e8;
    border-radius: 22px;
}

.creator-coming-soon h2 {
    margin: 0 0 12px;
    color: #3f3158;
    font-size: 30px;
    font-weight: 800;
}

.creator-coming-soon p:not(.creator-page-kicker) {
    max-width: 360px;
    margin: 0;
    line-height: 1.8;
}

@media (max-width: 1250px) {
    .creator-workspace {
        padding-right: 28px;
        padding-left: 28px;
    }
}

@media (max-width: 640px) {
    .creator-center-page.deposit-layer-open {
        min-width: 0;
        overflow-x: hidden;
    }

    .creator-center-page.deposit-layer-open .creator-center-shell {
        min-width: 0;
    }

    .creator-center-page.deposit-layer-open .creator-workspace {
        overflow: hidden;
    }
}

@media (prefers-reduced-motion: reduce) {
    .creator-center-page *,
    .creator-center-page *::before,
    .creator-center-page *::after {
        scroll-behavior: auto !important;
        transition-duration: .01ms !important;
        animation-duration: .01ms !important;
    }
}
</style>
